import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, invoiceStatusConfig } from 'entities';

import { LabeledInput, Button, Column, Row } from 'components/Blocks';
import { MonetaryInput } from 'components/Inputs';
import { UploadFileDropzone } from 'components/Uploads';

import { CircleXIcon, FileCheckIcon } from 'components/Icons';

import { InvoiceDate } from './Date';

import { useFormControl } from 'hooks';
import { useUser } from 'hooks/user';

import { getIsoDatePart } from 'utils/date';

import { InvoiceTabProps } from './types';
import { UploadedFile } from 'types';

export function UploadInvoice({
  invoice,
  overwrite,
  changeTab,
  closeModal,
}: InvoiceTabProps): JSX.Element {
  const user = useUser();
  const [invoiceFiles, setInvoiceFiles] = useState<UploadedFile[]>([]);

  const { t } = useLang();

  const { state, handleChange, dispatch } = useFormControl({
    accountantId: '',
    date: null as null | string,
    netValue: invoice.value,
  });

  const numericNetValue = Number(state.netValue);
  const numericValue = Number(invoice.value);

  const wrongNetValue = [
    numericNetValue > numericValue,
    numericNetValue === 0 ? numericValue !== 0 : false,
  ].some(Boolean);

  const uploadBlock = [
    !invoiceFiles.length,
    wrongNetValue,
    !Object.values(state).every(Boolean),
  ].some(Boolean);

  const uploadInvoice = actions.invoice.uploadInvoiceFile.use({
    block: uploadBlock,

    params: {
      accountantId: state.accountantId,
      netValue: state.netValue,
      invoicedAt: state.date ? getIsoDatePart(state.date) : '',
      files: invoiceFiles,
      invoice: invoice.id,
    },

    onSuccess: (_, { invoicedAt, accountantId, netValue, files }) => {
      overwrite((old) => ({
        ...old,

        accountantId,
        netValue,
        invoicedAt,

        status: invoiceStatusConfig.invoiced.code,

        files: files.map((file) => ({
          ...file,
          uploadedBy: { ...user, team: user.team.id },
          type: 'EMISSION_FILE',
          invoice: invoice.id,
        })),
      }));

      changeTab('data');
    },
  });

  return (
    <Column gap="8">
      <h6 className="text-center font-semibold">{t('upload-invoice')}</h6>

      <Column gap="8">
        <Row className="phone:flex-col phone:items-stretch" justifyContent="space-between">
          <MonetaryInput
            label={t('full-value') as string}
            disabled
            value={invoice.value}
            currency={invoice.currency}
            containerClassName="w-[80%] phone:w-full"
          />

          <MonetaryInput
            label={t('net-value') as string}
            onChange={handleChange}
            error={wrongNetValue}
            currency={invoice.currency}
            containerClassName="w-[80%] phone:w-full"
            currencyDisabled
            value={state.netValue}
            name="netValue"
          />
        </Row>

        <LabeledInput
          label={`${t('invoice')} #`}
          placeholder={t('type-here') as string}
          value={state.accountantId}
          name="accountantId"
          onChange={handleChange}
        />

        <InvoiceDate
          state={state}
          dispatch={dispatch}
          helperText={t('invoiced-date-helper')}
          label={t('invoiced-date')}
        />

        <UploadFileDropzone
          files={invoiceFiles}
          setFiles={setInvoiceFiles}
          uploadPrefix={`invoices/${invoice.id}` as 'invoiceId'}
          multiple={false}
          showInputOnDisabled={false}
          acceptedFormats={[
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/pdf',
          ]}
        />
      </Column>

      <Row justifyContent="space-between">
        <Button variant="outlined" onClick={closeModal}>
          <CircleXIcon />
          {t('cancel')}
        </Button>

        <Button disabled={uploadBlock} {...uploadInvoice.buttonProps}>
          {t('register-invoice')}
          <FileCheckIcon />
        </Button>
      </Row>
    </Column>
  );
}
